import { Box, Text } from '@chakra-ui/react';
import type { NormalizedResult } from 'hooks/siteSearch/types';
import { useSiteSearch } from 'hooks/siteSearch/useSiteSearch';
import { useAnalytics } from 'hooks/useAnalytics';
import { useEffect } from 'react';
import { useDebounce } from 'use-debounce';

import { ResultsSkeleton } from '../layouts/ResultsSkeleton';
import { Benefits } from '../results/Benefits';
import { BlogArticle } from '../results/BlogArticle';
import { Podcast } from '../results/Podcast';
import { Product } from '../results/Product';
import { Science } from '../results/Science';
import { SkincareIngredients } from '../results/SkincareIngredients';
import { Studies } from '../results/Studies';

interface SearchResultsProps {
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  handleClose: () => void;
}

export interface NavigationPayload {
  title: string;
  path: string;
}

const renderResult = (
  result: NormalizedResult,
  index: number,
  handleNavigate: (payload: NavigationPayload, position: number) => void,
) => {
  const position = index + 1;
  const onNavigate = (payload: NavigationPayload) =>
    handleNavigate(payload, position);

  switch (result._type) {
    case 'product':
      return (
        <Product key={`product-${index}`} {...result} onNavigate={onNavigate} />
      );
    case 'timeline_article':
      return (
        <BlogArticle
          key={`blog-${index}`}
          {...result}
          onNavigate={onNavigate}
        />
      );
    case 'timeline_podcast':
      return (
        <Podcast key={`podcast-${index}`} {...result} onNavigate={onNavigate} />
      );
    case 'timeline_scienceV2':
    case 'timeline_skincareScience':
      return <Science key={`science-${index}`} onNavigate={onNavigate} />;
    case 'timeline_studies':
      return <Studies key={`studies-${index}`} onNavigate={onNavigate} />;
    case 'timeline_skincareIngredients':
      return (
        <SkincareIngredients
          key={`ingredients-${index}`}
          onNavigate={onNavigate}
        />
      );
    case 'timeline_benefitsPage':
    case 'timeline_skincareBenefitsPage':
      return <Benefits key={`benefits-${index}`} onNavigate={onNavigate} />;
    default:
      return null;
  }
};

export function SearchResults({
  handleClose,
  searchTerm,
  setSearchTerm,
}: SearchResultsProps) {
  const [searchTermDebounced] = useDebounce(searchTerm, 1000);
  const { data: searchData, isLoading } = useSiteSearch(searchTermDebounced);
  const { analytics } = useAnalytics();

  const results = searchData?.results ?? [];
  const resultsLength = searchTermDebounced !== '' ? results.length : 0;
  const hasResults = resultsLength > 0;

  const showSkeleton = searchTerm !== searchTermDebounced || isLoading;
  const showResults = searchTerm === searchTermDebounced && hasResults;
  const showNoResults =
    searchTerm === searchTermDebounced && !hasResults && !isLoading;

  const handleNavigate = (
    { title, path }: NavigationPayload,
    position: number,
  ) => {
    handleClose();
    setSearchTerm('');

    analytics.searchClick({
      query: searchTermDebounced,
      results: resultsLength,
      path,
      title,
      position,
    });
  };

  useEffect(() => {
    if (showResults || showNoResults) {
      analytics.searchQuery({
        query: searchTermDebounced,
        results: resultsLength,
      });
    }
  }, [
    showResults,
    showNoResults,
    analytics,
    searchTermDebounced,
    resultsLength,
  ]);

  if (searchTerm === '') {
    return null;
  }

  return (
    <>
      {showSkeleton && <ResultsSkeleton />}
      {showResults && (
        <>
          {results.map((result, index) =>
            renderResult(result, index, handleNavigate),
          )}
        </>
      )}
      {showNoResults && (
        <Box pt="2xs" pb="sm">
          <Text as="h2" size="title">
            No results
          </Text>
        </Box>
      )}
    </>
  );
}
